const NC_EASINGS = {
  linear: (t) => t,
  easeIn: (t) => t * t,
  easeOut: (t) => t * (2 - t),
  ease: (t) => t * t * (3 - 2 * t),
  sineIn: (t) => 1 - Math.cos((t * Math.PI) / 2),
  sineOut: (t) => Math.sin((t * Math.PI) / 2),
  sine: (t) => -(Math.cos(Math.PI * t) - 1) / 2,
  quadIn: (t) => t * t,
  quadOut: (t) => 1 - (1 - t) * (1 - t),
  quad: (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),
  cubicIn: (t) => t * t * t,
  cubicOut: (t) => 1 - Math.pow(1 - t, 3),
  cubic: (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
  quartIn: (t) => Math.pow(t, 4),
  quartOut: (t) => 1 - Math.pow(1 - t, 4),
  quart: (t) => (t < 0.5 ? 8 * Math.pow(t, 4) : 1 - Math.pow(-2 * t + 2, 4) / 2),
  quintIn: (t) => Math.pow(t, 5),
  quintOut: (t) => 1 - Math.pow(1 - t, 5),
  quint: (t) => (t < 0.5 ? 16 * Math.pow(t, 5) : 1 - Math.pow(-2 * t + 2, 5) / 2),
  expoIn: (t) => (t === 0 ? 0 : Math.pow(2, 10 * t - 10)),
  expoOut: (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
  expo: (t) => (t === 0 ? 0 : t === 1 ? 1 : t < 0.5 ? Math.pow(2, 20 * t - 10) / 2 : (2 - Math.pow(2, -20 * t + 10)) / 2),
  circIn: (t) => 1 - Math.sqrt(1 - Math.pow(t, 2)),
  circOut: (t) => Math.sqrt(1 - Math.pow(t - 1, 2)),
  circ: (t) => (t < 0.5 ? (1 - Math.sqrt(1 - Math.pow(2 * t, 2))) / 2 : (Math.sqrt(1 - Math.pow(-2 * t + 2, 2)) + 1) / 2)
};

class NumberCounterDemo {
  constructor(root) {
    this.root = root;
    this.canvas = root.querySelector('.nc-demo__canvas');
    this.ctx = this.canvas.getContext('2d');
    this.replayButton = root.querySelector('.nc-demo__replay');
    this.replayLabel = root.querySelector('[data-playback-label]');
    this.fontStyleSelect = root.querySelector('[data-font-style]');
    this.progress = 0;

    this.W = 1920;
    this.H = 1080;

    this.state = this.defaultState();

    this.isCounting = false;
    this.countStart = 0;
    this.countFrom = this.state.animation.startValue;
    this.currentValue = this.state.animation.startValue;
    this.valueHistory = [{ t: performance.now(), v: this.currentValue }];
    this._blurValueOverride = null;

    this.charWiggles = [];
    this.sharedWiggle = null;
    this.loopToken = 0;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.setupCanvas();
    this.ctx.textAlign = 'left';
    this.ctx.textBaseline = 'middle';
    this.loadWatermark();

    this.bindControls();
    this.bindToggleButtons();
    this.bindFontStyle();
    this.bindReplay();
    this.bindScrubbing();
    this.bindReset();

    this.render();
  }

  defaultState() {
    return {
      text: {
        font: 'Arial',
        bold: false,
        italic: false,
        uppercase: false,
        smallCaps: false,
        size: 174,
        color: '#ffffff',
        strokeEnabled: true,
        strokeColor: '#000000',
        strokeWidth: 31.6,
        strokeStyle: 'perCharacter',
        tracking: 0.1,
        posX: 960,
        posY: 540,
        extra: ''
      },
      gradient: {
        enabled: false,
        p1: { x: 960, y: 0 },
        c1: '#000000',
        p2: { x: 960, y: 1080 },
        c2: '#ffffff',
        rampScatter: 0,
        blend: 0
      },
      wiggleSettings: {
        enabled: false,
        ampX: 4,
        ampY: 4,
        ampRot: 8,
        speed: 0.9,
        correlation: 50
      },
      shadow: {
        enabled: false,
        color: '#000000',
        opacity: 47.7,
        direction: 135,
        distance: 24.5,
        softness: 59.7
      },
      glow: {
        enabled: false,
        threshold: 60,
        radius: 76.4,
        intensity: 2.4
      },
      animation: {
        startValue: 0,
        endValue: 1000,
        duration: 1.7,
        decimals: 0,
        easing: 'quad',
        separator: ',',
        before: '',
        after: ''
      },
      motionBlur: {
        enabled: false,
        samples: 8,
        shutterAngle: 180
      },
      transform: {
        posX: 0,
        posY: 0,
        anchorX: 47.83,
        anchorY: -75.21,
        scaleX: 100,
        scaleY: 100,
        rotation: 0,
        opacity: 100
      }
    };
  }

  setupCanvas() {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = this.W * this.dpr;
    this.canvas.height = this.H * this.dpr;
    this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
  }

  loadWatermark() {
    const src = this.canvas.dataset.watermarkLogo;
    if (!src) return;
    const logo = new Image();
    logo.crossOrigin = 'anonymous';
    logo.onload = () => {
      this.watermarkImage = logo;
      this.render();
    };
    // Until the image loads (or if it fails), drawPreviewWatermark uses text.
    this.watermarkLoader = logo;
    logo.src = src;
  }

  getValueAtPath(obj, path) {
    return path.split('.').reduce((o, k) => o[k], obj);
  }

  setValueAtPath(obj, path, value) {
    const keys = path.split('.');
    let target = obj;
    for (let i = 0; i < keys.length - 1; i++) target = target[keys[i]];
    target[keys[keys.length - 1]] = value;
  }

  formatReadout(value, input) {
    if (input.type === 'checkbox' || input.type === 'color' || input.type === 'text' || input.tagName === 'SELECT') return '';
    const step = input.step === 'any' ? 0.1 : parseFloat(input.step || '1');
    const decimals = step < 1 ? 1 : 0;
    const unit = input.dataset.unit || '';
    return Number(value).toFixed(decimals) + unit;
  }

  updateRangeFill(input) {
    if (input.type !== 'range') return;
    const min = parseFloat(input.min || '0');
    const max = parseFloat(input.max || '100');
    const percent = ((parseFloat(input.value) - min) / (max - min)) * 100;
    input.style.setProperty('--fill', `${percent}%`);
  }

  bindControls() {
    const inputs = this.root.querySelectorAll('input[data-bind], select[data-bind], textarea[data-bind]');
    inputs.forEach((input) => {
      const path = input.dataset.bind;
      const current = this.getValueAtPath(this.state, path);

      if (input.type === 'checkbox') input.checked = current;
      else input.value = current;
      this.updateRangeFill(input);

      const readoutEl = input.closest('.nc-field')?.querySelector('.nc-field__value');
      if (readoutEl) readoutEl.textContent = this.formatReadout(current, input);

      input.addEventListener('input', () => {
        let value;
        if (input.type === 'checkbox') value = input.checked;
        else if (input.type === 'range' || input.type === 'number') value = parseFloat(input.value);
        else value = input.value;

        // A number field can be temporarily empty while editing; never render NaN.
        if (typeof value === 'number' && !Number.isFinite(value)) return;

        this.setValueAtPath(this.state, path, value);
        this.updateRangeFill(input);
        if (readoutEl) readoutEl.textContent = this.formatReadout(value, input);
        if (path.startsWith('animation.')) {
          this.countFrom = this.state.animation.startValue;
          this.countStart = performance.now() - this.progress * this.durationMs();
          this.sampleProgress();
          this.valueHistory = [{ t: performance.now(), v: this.currentValue }];
        }
        this.render();
      });
      if (input.type === 'number') input.addEventListener('change', () => {
        if (!Number.isFinite(parseFloat(input.value))) input.value = this.getValueAtPath(this.state, path);
      });
    });
  }

  bindToggleButtons() {
    const buttons = this.root.querySelectorAll('button[data-bind]');
    buttons.forEach((btn) => {
      const path = btn.dataset.bind;
      btn.setAttribute('aria-pressed', String(this.getValueAtPath(this.state, path)));
      btn.addEventListener('click', () => {
        const next = !this.getValueAtPath(this.state, path);
        this.setValueAtPath(this.state, path, next);
        btn.setAttribute('aria-pressed', String(next));
        this.syncFontStyle();
        this.render();
      });
    });
  }

  syncFontStyle() {
    if (!this.fontStyleSelect) return;
    const { bold, italic } = this.state.text;
    this.fontStyleSelect.value = bold ? (italic ? 'bold-italic' : 'bold') : (italic ? 'italic' : 'regular');
  }

  bindFontStyle() {
    this.syncFontStyle();
    this.fontStyleSelect?.addEventListener('change', () => {
      const style = this.fontStyleSelect.value;
      this.state.text.bold = style.includes('bold');
      this.state.text.italic = style.includes('italic');
      ['bold', 'italic'].forEach((name) => {
        this.root.querySelector(`[data-bind="text.${name}"]`)?.setAttribute('aria-pressed', String(this.state.text[name]));
      });
      this.render();
    });
  }

  bindReplay() {
    this.replayButton.addEventListener('click', () => {
      if (this.isCounting) this.seek(this.progress);
      else if (this.progress > 0 && this.progress < 1) this.resumeCount();
      else this.startCount();
    });
  }

  syncPlaybackButton() {
    const label = this.isCounting ? 'Pause' : this.progress >= 1 ? 'Replay' : 'Play';
    if (this.playbackLabel === label) return;
    this.playbackLabel = label;
    // the words in the shop's language from the button's data-i18n-* (English as the fallback)
    const words = this.replayButton.dataset;
    this.replayLabel.textContent = words[`i18n${label}`] || label;
    this.replayButton.dataset.playback = label.toLowerCase();
    this.replayButton.setAttribute('aria-label', words[`i18n${label}Label`] || `${label} counter`);
  }

  resumeCount() {
    this.loopToken += 1;
    const token = this.loopToken;
    this.countFrom = this.state.animation.startValue;
    this.countStart = performance.now() - this.progress * this.durationMs();
    this.isCounting = true;
    this.render();
    requestAnimationFrame((now) => this.loop(now, token));
  }

  durationMs() {
    return Math.max(this.state.animation.duration, 0.05) * 1000;
  }

  sampleProgress() {
    const a = this.state.animation;
    const eased = (NC_EASINGS[a.easing] || NC_EASINGS.quad)(this.progress);
    this.currentValue = a.startValue + (a.endValue - a.startValue) * eased;
  }

  seek(progress) {
    this.loopToken += 1;
    this.isCounting = false;
    this.progress = Math.min(1, Math.max(0, progress));
    this.countFrom = this.state.animation.startValue;
    this.sampleProgress();
    this.valueHistory = [{ t: performance.now(), v: this.currentValue }];
    this.render();
  }

  reset() {
    this.state = this.defaultState();
    this.charWiggles = [];
    this.sharedWiggle = null;
    this.root.querySelectorAll('input[data-bind], select[data-bind], textarea[data-bind]').forEach((input) => {
      const value = this.getValueAtPath(this.state, input.dataset.bind);
      if (input.type === 'checkbox') input.checked = value;
      else input.value = value;
      this.updateRangeFill(input);
      const readout = input.closest('.nc-field')?.querySelector('.nc-field__value');
      if (readout) readout.textContent = this.formatReadout(value, input);
    });
    this.root.querySelectorAll('button[data-bind]').forEach((button) => {
      button.setAttribute('aria-pressed', String(this.getValueAtPath(this.state, button.dataset.bind)));
    });
    this.syncFontStyle();
    this.seek(0);
  }

  bindReset() {
    this.root.querySelector('.nc-demo__reset')?.addEventListener('click', () => {
      this.reset();
    });
  }

  // Adobe-style scrubbable numeric fields: press and drag left/right to change the value, exactly
  // like dragging an X/Y or slider readout in the real Essential Graphics panel. A plain click (no
  // meaningful movement) falls through to the input's normal focus-and-type behavior.
  attachScrub(el, { getValue, setValue, sensitivity }) {
    let dragging = false;
    let moved = false;
    let startX = 0;
    let startValue = 0;

    el.classList.add('is-scrubbable');

    el.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startValue = getValue();
      el.setPointerCapture(e.pointerId);
    });

    el.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) < 3) return;

      if (!moved) {
        moved = true;
        el.classList.add('is-scrubbing');
        if (document.activeElement === el) el.blur();
      }
      e.preventDefault();
      setValue(startValue + dx * sensitivity);
    });

    const stop = () => {
      if (!dragging) return;
      dragging = false;
      el.classList.remove('is-scrubbing');
    };
    el.addEventListener('pointerup', stop);
    el.addEventListener('pointercancel', stop);

    // A drag that ends back on the element still fires 'click' natively - swallow just that one so
    // a drag never also opens/focuses the field as if it had been a plain click.
    el.addEventListener('click', (e) => {
      if (moved) {
        e.preventDefault();
        moved = false;
      }
    });
  }

  bindScrubbing() {
    this.root.querySelectorAll('.nc-field__point-input').forEach((input) => {
      this.attachScrub(input, {
        getValue: () => parseFloat(input.value) || 0,
        setValue: (v) => {
          input.value = Math.round(v * 100) / 100;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        },
        sensitivity: 1
      });
    });

    this.root.querySelectorAll('.nc-field__value').forEach((readout) => {
      const range = readout.closest('.nc-field')?.querySelector('.nc-field__range');
      if (!range) return;
      const min = parseFloat(range.min);
      const max = parseFloat(range.max);
      const step = range.step === 'any' ? (max - min) / 1000 : parseFloat(range.step || '1');
      // ~260px drag traverses the field's full range - feels natural in this panel's width.
      const sensitivity = (max - min) / 260;

      this.attachScrub(readout, {
        getValue: () => parseFloat(range.value) || 0,
        setValue: (v) => {
          const stepped = Math.round(v / step) * step;
          range.value = Math.min(max, Math.max(min, stepped));
          range.dispatchEvent(new Event('input', { bubbles: true }));
        },
        sensitivity
      });
    });
  }

  startCount() {
    // Clicking Replay while a previous run (or its idle-wiggle tail) is still mid-flight used to
    // let two rAF chains drive this.currentValue at once, each computing its own progress from a
    // different countStart - the visible symptom was the animation looking like it "didn't work"
    // (stalling, jumping, or settling on the wrong value) roughly however often the click landed
    // mid-animation. Each call now mints a token; a chain checks it's still current before every
    // step and quietly stops the instant a newer call supersedes it, so only one chain ever runs.
    this.loopToken += 1;
    const token = this.loopToken;
    // Replay means restart the count, not "animate from wherever it happens to be sitting" - using
    // currentValue here was the real bug: after a run completes, currentValue already equals
    // endValue, so the next Replay animated from endValue to endValue (a real rAF loop, animating
    // across a zero-length range, so nothing visibly moved) unless endValue had just been edited to
    // something new, which was the only case that ever produced a visible gap to animate across.
    this.currentValue = this.state.animation.startValue;
    this.progress = 0;
    this.countFrom = this.currentValue;
    this.countStart = performance.now();
    this.isCounting = true;
    this.valueHistory = [{ t: this.countStart, v: this.currentValue }];
    this.render();
    requestAnimationFrame((now) => this.loop(now, token));
  }

  loop(now, token) {
    if (token !== this.loopToken) return;

    const a = this.state.animation;
    const durationMs = Math.max(a.duration, 0.05) * 1000;
    const elapsed = now - this.countStart;
    const progress = Math.min(elapsed / durationMs, 1);
    this.progress = progress;
    this.isCounting = progress < 1;
    const eased = (NC_EASINGS[a.easing] || NC_EASINGS.quad)(progress);
    this.currentValue = this.countFrom + (a.endValue - this.countFrom) * eased;

    this.valueHistory.push({ t: now, v: this.currentValue });
    const cutoff = now - 120;
    while (this.valueHistory.length > 2 && this.valueHistory[1].t < cutoff) this.valueHistory.shift();

    this.updateWiggle(now);
    this.render(now);

    if (progress < 1) {
      requestAnimationFrame((n) => this.loop(n, token));
    }
    else {
      this.isCounting = false;
      if (this.state.wiggleSettings.enabled && !this.reducedMotion) {
        requestAnimationFrame((n) => this.idleLoop(n, token));
      }
    }
  }

  idleLoop(now, token) {
    if (token !== this.loopToken || this.isCounting) return;
    if (!this.state.wiggleSettings.enabled || this.reducedMotion) {
      this.render(now);
      return;
    }
    this.updateWiggle(now);
    this.render(now);
    requestAnimationFrame((n) => this.idleLoop(n, token));
  }

  createWiggleNode(now) {
    return {
      prevX: 0, prevY: 0, prevRot: 0,
      nextX: 0, nextY: 0, nextRot: 0,
      x: 0, y: 0, rot: 0,
      // Randomized starting phase so characters don't all re-roll their segment in lockstep.
      segStart: now - Math.random() * 300,
      segDuration: 300
    };
  }

  // Segments are interpolated with smoothstep (zero velocity at both ends), so one segment hands
  // off to the next with no sudden change in speed or direction - this is what reads as smooth,
  // flowing wiggle rather than the visible "snap toward a new target" of a plain exponential ease.
  advanceWiggleNode(node, now, w) {
    if (!w.enabled || this.reducedMotion) {
      node.x = node.y = node.rot = 0;
      return;
    }
    let t = (now - node.segStart) / node.segDuration;
    if (t >= 1) {
      node.prevX = node.nextX;
      node.prevY = node.nextY;
      node.prevRot = node.nextRot;
      node.nextX = (Math.random() * 2 - 1) * w.ampX;
      node.nextY = (Math.random() * 2 - 1) * w.ampY;
      node.nextRot = (Math.random() * 2 - 1) * w.ampRot;
      const freq = Math.max(w.speed, 0.05);
      node.segStart = now;
      node.segDuration = (1000 / freq) * (0.7 + Math.random() * 0.6);
      t = 0;
    }
    t = Math.min(Math.max(t, 0), 1);
    const eased = t * t * (3 - 2 * t);
    node.x = node.prevX + (node.nextX - node.prevX) * eased;
    node.y = node.prevY + (node.nextY - node.prevY) * eased;
    node.rot = node.prevRot + (node.nextRot - node.prevRot) * eased;
  }

  // AE's per-character text animators run wiggle independently on each glyph by default - Correlation
  // is the Wiggly Selector meaning: 0 = every character fully independent, 100 = every character
  // shares the exact same motion (moves in unison). Blended per-character in drawTracked.
  updateWiggle(now) {
    const w = this.state.wiggleSettings;
    const charCount = Array.from(this.displayCountString()).length;

    if (!this.sharedWiggle) this.sharedWiggle = this.createWiggleNode(now);
    this.advanceWiggleNode(this.sharedWiggle, now, w);

    for (let i = 0; i < charCount; i++) {
      if (!this.charWiggles[i]) this.charWiggles[i] = this.createWiggleNode(now);
      this.advanceWiggleNode(this.charWiggles[i], now, w);
    }
  }

  hexWithAlpha(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  formatNumber(value) {
    const a = this.state.animation;
    const fixed = Math.abs(value).toFixed(a.decimals);
    const parts = fixed.split('.');
    let intPart = parts[0];
    const decPart = parts[1];
    if (a.separator) intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, a.separator);
    const sign = value < 0 ? '-' : '';
    return a.before + sign + intPart + (decPart ? '.' + decPart : '') + a.after;
  }

  displayCountString() {
    const value = this._blurValueOverride != null ? this._blurValueOverride : this.currentValue;
    let str = this.formatNumber(value);
    if (this.state.text.uppercase && !this.state.text.smallCaps) str = str.toUpperCase();
    return str;
  }

  glyphFont(ch) {
    const s = this.state.text;
    const isLower = /[a-z]/.test(ch);
    const size = s.smallCaps && isLower ? s.size * 0.8 : s.size;
    const style = s.italic ? 'italic' : 'normal';
    const weight = s.bold ? '700' : '400';
    return {
      font: `${style} ${weight} ${size}px ${s.font}, sans-serif`,
      char: s.smallCaps ? ch.toUpperCase() : ch
    };
  }

  // Shared per-character measuring/positioning/wiggle walk - drawTracked calls back with a single
  // mode (fill or stroke) for each glyph; the "Per Character Palette" stroke style calls back twice
  // per glyph (its own stroke then its own fill) before advancing, which drawTracked's own two
  // separate full-string passes can't express.
  forEachChar(ctx, str, callback) {
    const s = this.state.text;
    const trackingPx = s.size * (s.tracking / 1000);
    const chars = Array.from(str);
    const widths = chars.map((ch) => {
      const g = this.glyphFont(ch);
      ctx.font = g.font;
      const key = `${g.font}\u0000${g.char}`;
      if (!this.glyphWidths.has(key)) this.glyphWidths.set(key, ctx.measureText(g.char).width);
      return this.glyphWidths.get(key);
    });
    const totalWidth = widths.reduce((sum, w) => sum + w, 0) + trackingPx * (chars.length - 1);

    let x = -totalWidth / 2;
    chars.forEach((ch, i) => {
      const g = this.glyphFont(ch);
      ctx.font = g.font;
      const cw = this.state.wiggleSettings.enabled ? this.charWiggles[i] : null;
      const shared = this.sharedWiggle;
      const corr = this.state.wiggleSettings.correlation / 100;
      const wx = cw ? cw.x + (shared.x - cw.x) * corr : 0;
      const wy = cw ? cw.y + (shared.y - cw.y) * corr : 0;
      const wrot = cw ? cw.rot + (shared.rot - cw.rot) * corr : 0;
      const charCenterX = x + widths[i] / 2;

      callback(g, x, charCenterX, wx, wy, wrot);

      x += widths[i] + trackingPx;
    });
  }

  drawGlyph(ctx, g, x, charCenterX, wx, wy, wrot, mode) {
    if (wx || wy || wrot) {
      ctx.save();
      ctx.translate(charCenterX, 0);
      ctx.rotate((wrot * Math.PI) / 180);
      ctx.translate(-charCenterX + wx, wy);
      if (mode === 'stroke') ctx.strokeText(g.char, x, 0);
      else ctx.fillText(g.char, x, 0);
      ctx.restore();
    }
    else if (mode === 'stroke') ctx.strokeText(g.char, x, 0);
    else ctx.fillText(g.char, x, 0);
  }

  drawTracked(ctx, str, mode) {
    this.forEachChar(ctx, str, (g, x, ccx, wx, wy, wrot) => {
      this.drawGlyph(ctx, g, x, ccx, wx, wy, wrot, mode);
    });
  }

  drawGlow(ctx) {
    const g = this.state.glow;
    if (!g.enabled) return;
    const str = this.displayCountString();
    const blur = (g.radius / 1000) * 90;
    const passes = Math.max(1, Math.round(g.intensity));
    const alpha = Math.min(1, g.intensity / 4) * (g.threshold / 100 + 0.3);

    ctx.save();
    ctx.filter = `blur(${blur}px)`;
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = this.state.text.color;
    ctx.globalAlpha = alpha;
    for (let i = 0; i < passes; i++) {
      this.drawTracked(ctx, str, 'fill');
    }
    ctx.restore();
  }

  drawMotionBlurTrail(ctx, now) {
    const mb = this.state.motionBlur;
    if (!mb.enabled || !this.isCounting || this.reducedMotion) return;
    const samples = Math.min(mb.samples, 16);
    const windowMs = (mb.shutterAngle / 360) * (1000 / 30);

    for (let i = samples; i >= 1; i--) {
      const t = now - (i / samples) * windowMs;
      ctx.save();
      ctx.globalAlpha *= (1 - i / samples) * 0.4;
      ctx.fillStyle = this.state.text.color;
      this._blurValueOverride = this.valueAt(t);
      this.drawTracked(ctx, this.displayCountString(), 'fill');
      this._blurValueOverride = null;
      ctx.restore();
    }
  }

  valueAt(t) {
    const hist = this.valueHistory;
    if (hist.length === 0) return this.currentValue;
    let result = hist[0].v;
    for (const p of hist) {
      if (p.t <= t) result = p.v;
      else break;
    }
    return result;
  }

  drawMainText(ctx, gradientFill) {
    const s = this.state.text;
    const str = this.displayCountString();

    if (this.state.shadow.enabled) {
      const rad = (this.state.shadow.direction * Math.PI) / 180;
      ctx.shadowColor = this.hexWithAlpha(this.state.shadow.color, this.state.shadow.opacity / 100);
      ctx.shadowBlur = this.state.shadow.softness;
      ctx.shadowOffsetX = Math.cos(rad) * this.state.shadow.distance;
      ctx.shadowOffsetY = Math.sin(rad) * this.state.shadow.distance;
    }

    const hasStroke = s.strokeEnabled && s.strokeWidth > 0;
    if (hasStroke) {
      ctx.strokeStyle = s.strokeColor;
      ctx.lineWidth = s.strokeWidth;
      ctx.lineJoin = 'round';
    }
    ctx.fillStyle = s.color;

    if (!hasStroke) {
      this.drawTracked(ctx, str, 'fill');
    }
    else if (s.strokeStyle === 'allStrokesOverFills') {
      this.drawTracked(ctx, str, 'fill');
      ctx.shadowColor = 'transparent';
      this.drawTracked(ctx, str, 'stroke');
    }
    else if (s.strokeStyle === 'allFillsOverStrokes') {
      // Stroke drawn first (wider) so the fill sits on top and the stroke only shows as a clean outer ring.
      this.drawTracked(ctx, str, 'stroke');
      ctx.shadowColor = 'transparent';
      this.drawTracked(ctx, str, 'fill');
    }
    else {
      // 'perCharacter' (default): each character's own stroke+fill drawn together before the next,
      // so a wide stroke on one glyph can never sit on top of a neighboring glyph's fill.
      this.forEachChar(ctx, str, (g, x, ccx, wx, wy, wrot) => {
        this.drawGlyph(ctx, g, x, ccx, wx, wy, wrot, 'stroke');
        this.drawGlyph(ctx, g, x, ccx, wx, wy, wrot, 'fill');
      });
    }

    ctx.shadowColor = 'transparent';

    if (gradientFill) {
      // A gradient's coordinates are evaluated against whatever transform is active at PAINT time,
      // not when createLinearGradient was called - so painting it here, inside the already-applied
      // translate/rotate/scale/anchor transform, would reinterpret its 1920x1080-space coordinates
      // through that local space and mostly wash out to a single flat color. Instead: draw the text
      // once more as an opaque mask, then use source-in to composite the gradient - built and painted
      // in absolute canvas space (transform reset) - only into the pixels that mask just occupied.
      // This also matches AE's own Ramp effect, which paints in fixed comp space regardless of the
      // layer's own Transform.
      ctx.save();
      ctx.globalAlpha *= 1 - this.state.gradient.blend / 100;
      ctx.fillStyle = s.color;
      this.drawTracked(ctx, str, 'fill');
      ctx.globalCompositeOperation = 'source-in';
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.fillStyle = gradientFill;
      ctx.fillRect(0, 0, this.W, this.H);
      ctx.restore();
    }
  }

  drawPreviewWatermark(ctx) {
    const logo = this.watermarkImage;
    const size = Math.max(120, this.state.text.size * 2.2);
    ctx.save();
    ctx.globalCompositeOperation = 'destination-over';
    ctx.globalAlpha = 0.5;
    ctx.filter = 'none';
    ctx.shadowColor = 'transparent';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#151515';
    ctx.fillStyle = '#ffffff';
    let offsetX = 0, offsetY = 0, rotation = 0, count = 0;
    this.forEachChar(ctx, this.displayCountString(), (glyph, x, centerX, wx, wy, angle) => {
      if (!glyph.char.trim()) return;
      offsetX += wx;
      offsetY += wy;
      rotation += angle;
      count += 1;
    });
    if (count) {
      ctx.translate(offsetX / count, offsetY / count);
      ctx.rotate((rotation / count) * Math.PI / 180);
    }
    if (logo && logo.naturalWidth > 0 && logo.naturalHeight > 0) {
      const ratio = logo.naturalWidth / logo.naturalHeight;
      const width = ratio >= 1 ? size : size * ratio;
      const height = ratio >= 1 ? size / ratio : size;
      ctx.drawImage(logo, -width / 2, -height / 2, width, height);
    } else {
      ctx.font = '600 32px Arial, sans-serif';
      ctx.strokeText('Adobe Basics', 0, 0, size);
      ctx.fillText('Adobe Basics', 0, 0, size);
    }
    ctx.restore();
  }

  render(now) {
    this.glyphWidths = new Map();
    this.syncPlaybackButton();
    const ctx = this.ctx;
    const s = this.state;
    const time = now || performance.now();
    ctx.clearRect(0, 0, this.W, this.H);

    // Built here in absolute canvas space (see the source-in compositing note in drawMainText for why).
    let gradientFill = null;
    if (s.gradient.enabled) {
      const scatter = (Math.random() - 0.5) * s.gradient.rampScatter;
      gradientFill = ctx.createLinearGradient(
        s.gradient.p1.x + scatter,
        s.gradient.p1.y + scatter,
        s.gradient.p2.x - scatter,
        s.gradient.p2.y - scatter
      );
      gradientFill.addColorStop(0, s.gradient.c1);
      gradientFill.addColorStop(1, s.gradient.c2);
    }

    ctx.save();
    const cx = s.text.posX + s.transform.posX;
    const cy = s.text.posY + s.transform.posY;
    ctx.translate(cx, cy);
    ctx.rotate((s.transform.rotation * Math.PI) / 180);
    ctx.scale(s.transform.scaleX / 100, s.transform.scaleY / 100);
    ctx.translate(-s.transform.anchorX, -s.transform.anchorY);
    ctx.globalAlpha = s.transform.opacity / 100;

    this.drawMotionBlurTrail(ctx, time);
    this.drawGlow(ctx);
    this.drawMainText(ctx, gradientFill);
    this.drawPreviewWatermark(ctx);

    ctx.restore();
  }

  destroy() {
    this.loopToken += 1;
    if (this.watermarkLoader) this.watermarkLoader.onload = null;
  }
}

function initNumberCounterDemos(scope) {
  (scope || document).querySelectorAll('[data-number-counter-demo]').forEach((root) => {
    if (root.dataset.ncInitialized) return;
    root.dataset.ncInitialized = 'true';
    root.ncDemo = new NumberCounterDemo(root);
  });
}

initNumberCounterDemos();
document.addEventListener('shopify:section:load', (event) => initNumberCounterDemos(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-number-counter-demo]').forEach((root) => root.ncDemo?.destroy());
});

